import AgricultureIcon from '@mui/icons-material/Agriculture'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import LabelIcon from '@mui/icons-material/LocalOfferOutlined'
import PersonAddIcon from '@mui/icons-material/PersonAddOutlined'
import { Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { GridRowId, useGridApiContext } from '@mui/x-data-grid-pro'
import { TFunction } from 'i18next'
import { MouseEventHandler, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthUserPermission, FieldModel } from '@cibo/core'
import { Can } from '@cibo/profile'
import { PaginatedFieldsContext } from '../..'
import { useLandManagement } from '../LandManagementProvider'

const BulkActionButton = ({
  disabled,
  icon,
  name,
  action,
  unactionableFields,
  someSelected,
  tooManySelected,
  t,
}: {
  disabled?: boolean
  icon: ReactNode
  name: string
  action: MouseEventHandler<HTMLButtonElement>
  unactionableFields: { name: string; resourceId: string }[]
  someSelected: boolean
  tooManySelected: boolean
  t: TFunction
}) => (
  <Tooltip
    title={
      unactionableFields.length > 0 ? (
        <Stack>
          <Typography>{t(`cannot${name}`)}</Typography>
          <ul>
            {unactionableFields.map(field => (
              <Typography key={field?.resourceId} component="li" variant="caption">
                {field?.name}
              </Typography>
            ))}
          </ul>
        </Stack>
      ) : tooManySelected ? (
        `${t('tooManySelected')}`
      ) : (
        `${t(name)}`
      )
    }
    placement="bottom"
    arrow
  >
    <div>
      <IconButton
        onClick={action}
        disabled={disabled || !someSelected || unactionableFields.length > 0 || tooManySelected}
        data-testid={`lm-bulk-action-${name}`}
      >
        {icon}
      </IconButton>
    </div>
  </Tooltip>
)

export const LandManagerToolbar = ({ navigation }: { navigation: any }) => {
  const { t } = useTranslation('@cibo/landmanager/LandManagerToolbar')
  const { onDelete, onLabel, onAssignFields } = useContext(PaginatedFieldsContext)
  const { openAssignFarmDialog } = useLandManagement()
  const { current: gridApi } = useGridApiContext()
  const { current: selectionCache } = useRef(
    gridApi.getSelectedRows() as Map<GridRowId, FieldModel>
  )
  const [selectedFields, setSelectedFields] = useState<FieldModel[]>([])

  /**
   * Track the permissions of the selected rows.  Unfortunately the mui data-grid-pro
   * selection model does not track selected rows across pages when using server-side
   * pagination.  This behavior is accounted for in PaginatedFieldsTable, but the
   * selectionModel does not provide "off-page" values for the selected keys
   */
  useEffect(() => {
    // @ts-ignore not sure how to type the rows array for model
    gridApi.getSelectedRows().forEach((value, key) => !!value && selectionCache.set(key, value))
    setSelectedFields(
      Array.from(gridApi.getSelectedRows().keys()).map(key => selectionCache.get(key) as FieldModel)
    )
  }, [Array.from(gridApi.getSelectedRows().keys()).join()])

  const someSelected = selectedFields.length > 0
  const tooManySelected = selectedFields.length > 500

  return (
    <>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem />}>
          {!!navigation && (
            <Stack direction="row" alignItems="center" justifyContent="flex-start">
              {navigation}
            </Stack>
          )}
          <Stack direction="row" spacing={1}>
            <BulkActionButton
              name="label"
              action={onLabel}
              icon={<LabelIcon />}
              someSelected={someSelected}
              tooManySelected={tooManySelected}
              unactionableFields={selectedFields.filter(field => !field?.can(['edit_meta']))}
              t={t}
            />

            <Can useAny={AuthUserPermission.FIELDS_OWNER_MODIFY}>
              <BulkActionButton
                name="grower"
                action={onAssignFields}
                icon={<PersonAddIcon />}
                someSelected={someSelected}
                tooManySelected={tooManySelected}
                // disabled={!!lockedFields}
                unactionableFields={selectedFields.filter(field => !field?.can(['assign_owner']))}
                t={t}
              />
            </Can>

            <Can useAny={AuthUserPermission.FARMS_MANAGE}>
              <BulkActionButton
                name="assignFarm"
                action={() =>
                  openAssignFarmDialog({
                    resourceIds: selectedFields.map(({ resourceId }) => resourceId),
                  })
                }
                icon={<AgricultureIcon />}
                someSelected={someSelected}
                tooManySelected={tooManySelected}
                unactionableFields={selectedFields.filter(field => !field?.can(['edit_meta']))}
                disabled={selectedFields.some(
                  field => field?.owner?.userId !== selectedFields?.[0]?.owner?.userId
                )}
                t={t}
              />
            </Can>
          </Stack>
          <Stack direction="row" spacing={1}>
            <BulkActionButton
              name="delete"
              action={e => onDelete()}
              icon={<DeleteOutline />}
              someSelected={someSelected}
              tooManySelected={tooManySelected}
              unactionableFields={selectedFields.filter(field => !field?.can(['delete']))}
              data-testid="lm-bulk-action-delete"
              t={t}
            />
          </Stack>
        </Stack>
      </Stack>
    </>
  )
}
